import React, { useMemo, useState, useRef, useLayoutEffect, FC } from 'react';
import { observer } from 'mobx-react-lite';
import clone from 'lodash/clone';
import { useStore } from 'hooks';
import { useCloseMenuOnScroll } from '../../../../utils';
import { IObject } from 'types';
import { Coords, FiasSearchProps } from '../../../../types';
import { InlineSpinner } from 'components';
import { MenuOptions, SecondMenuOptions, MenuFooter } from './components';
import { Wrapper, OptionsWrapper, SpinnerWrapper } from './menu-list.styled';

type MenuListProps = {
  openMenu: boolean;
  coords: Coords;
  closePortal: () => void;
} & Pick<
  FiasSearchProps,
  'withSectors' | 'flat' | 'selectOnlyHouses' | 'disabledHouses'
>;

export const MenuList: FC<MenuListProps> = observer(
  ({
    flat,
    openMenu,
    selectOnlyHouses,
    coords,
    closePortal,
    withSectors,
    disabledHouses,
  }) => {
    const { fias } = useStore();
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>(0);
    useCloseMenuOnScroll(ref, () => closePortal());

    useLayoutEffect(() => {
      if (ref.current) {
        setHeight(ref.current.clientHeight);
      }
    }, [openMenu]);

    const searchGroups = (name: string): IObject[] => {
      let params: IObject[] = clone(
        withSectors ? fias.houseGroupsWithSectorsFiltered : fias.houseGroups
      );
      // toLowerCase для поиска вне зависимости от регистра
      params = params.filter((item: IObject): boolean =>
        item.title.toLowerCase().includes(name.toLowerCase())
      );

      return params;
    };

    const options: IObject[] = useMemo((): IObject[] => {
      switch (fias.currentFiasSearchType) {
        case 'houses':
          return withSectors
            ? fias.currentValueSearch.length >= 3
              ? fias.searchTreeWithSectorsFiltered
              : fias.treeWithSectorsFiltered
            : fias.currentValueSearch.length >= 3
            ? fias.searchTree
            : fias.tree;
        case 'groups':
          return fias.currentValueSearch.length >= 1
            ? searchGroups(fias.currentValueSearch)
            : withSectors
            ? fias.houseGroupsWithSectorsFiltered
            : fias.houseGroups;
        default:
          return fias.tree;
      }
    }, [
      fias.currentFiasSearchType,
      fias.currentValueSearch,
      fias.searchTree,
      fias.searchTreeWithSectorsFiltered,
    ]);

    return (
      <Wrapper ref={ref} coords={coords} height={height}>
        {fias.statuses.fiasTree.pending ||
        fias.statuses.searchTree.pending ||
        fias.statuses.houseGroups.pending ? (
          <SpinnerWrapper>
            <InlineSpinner style={{ margin: 'auto auto' }} />
          </SpinnerWrapper>
        ) : (
          <OptionsWrapper>
            <MenuOptions
              selectOnlyHouses={selectOnlyHouses}
              options={options}
              flat={flat}
              disabledHouses={disabledHouses}
              withSectors={withSectors}
            />
            {fias.showSecondOptionsField && (
              <SecondMenuOptions flat={flat} disabledHouses={disabledHouses} />
            )}
          </OptionsWrapper>
        )}
        {!(
          (fias.currentValueSearch.length >= 3 &&
            !fias.searchTree.length &&
            fias.currentFiasSearchType === 'houses') ||
          (fias.currentFiasSearchType === 'groups' &&
            fias.currentValueSearch.length >= 1 &&
            !searchGroups(fias.currentValueSearch).length) ||
          fias.statuses.fiasTree.pending ||
          fias.statuses.searchTree.pending
        ) && <MenuFooter flat={flat} closePortal={closePortal} />}
      </Wrapper>
    );
  }
);
